import classNames from 'classnames';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Row } from 'react-bootstrap';
import { FiFilter } from "react-icons/fi";
import { MdClose } from "react-icons/md";
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { associations, contentStatus, itemFilterTypes } from '../../../constants';
import { businessSelector } from '../../../state/modules/business/selectors';
import Loading from '../../pages/loading';
import AssociationFilter from './partials/association-filter';
import ContentLengthFilter from './partials/content-length-filter';
import ContentTypeFilter from './partials/content-type-filter';
import CpdCategoryFilter from './partials/cpd-category-filter';
import EventDurationFilter from './partials/event-duration-filter';
import IndustryCpdCategoryFilter from './partials/industry-cpd-category-filter';
import LocationFilter from './partials/location-filter';
import ProgressGoalFilter from './partials/progress-goal-filter';
import PublisherFilter from './partials/publisher-filter';
import StatusFilter from './partials/status-filter';
import TaskStatusFilter from './partials/task-status-filter';
import ContentBookmarkFilter from './partials/content-bookmark-filter';

const getPublishers = (state) => {
  const userPortalPublishers = state.publisher.publishers || []
  const publisherPortalPublishers = state.publisherPortal.publisher.publishers || []
  if (userPortalPublishers.length > publisherPortalPublishers) {
    return userPortalPublishers
  } else {
    return publisherPortalPublishers
  }
}

const FilterSlider = compose(
  connect(
    (state) => ({
      isFetchingCpdCategory: state.industry.cpdCategorysIsFetching,
      industryCpdCategorys: state.industry.cpdCategorys,
      publishers: getPublishers(state),
      business: businessSelector(state)
    })
  ),
  branch(
    ({ isFetchingCpdCategory, publishers, activeFilters }) => {
      const publisherFilterNotReady = fp.includes(itemFilterTypes.publisher)(activeFilters) && (!publishers || publishers.length === 0)
      const cpdCategoryFilterNotReady = isFetchingCpdCategory
      return publisherFilterNotReady || cpdCategoryFilterNotReady
    },
    renderComponent(Loading)
  )
)(({
  show,
  industryCpdCategorys,
  publishers,
  business,
  updateFilter,
  cancelFilter,
  clearCounter,
  activeFilters,
  publisherOnly,
  closeSlider,
  toggleSlider,
  filter
}) => {
  return <div key={clearCounter} id='filter-slider' className={classNames('flexbox vertical', { show })}>
    <Row className='no-gutter'>
      <h2>
        <FiFilter />Filter by <MdClose id='close-slider-button' onClick={toggleSlider} />
      </h2>
    </Row>
    <Row className='filters no-gutter'>
      {
        fp.includes(itemFilterTypes.industryCpdCategory)(activeFilters) ?
          <IndustryCpdCategoryFilter {...{
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.cpdCategory)(activeFilters) ?
          <CpdCategoryFilter {...{
            cpdCategorys: industryCpdCategorys,
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.publisher)(activeFilters) ?
          <PublisherFilter {...{
            business: publisherOnly ? undefined : business,
            publishers: publishers,
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.association)(activeFilters) ?
          <AssociationFilter {...{
            associations,
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.contentLength)(activeFilters) ?
          <ContentLengthFilter {...{
            onChange: updateFilter,
            contentLengthFilter: filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.eventDuration)(activeFilters) ?
          <EventDurationFilter {...{
            onChange: updateFilter,
            eventDurationFilter: filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.contentType)(activeFilters) ?
          <ContentTypeFilter {...{
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.status)(activeFilters) ?
          <StatusFilter {...{
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.taskStatus)(activeFilters) ?
          <TaskStatusFilter {...{
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.location)(activeFilters) ?
          <LocationFilter {...{
            onChange: updateFilter,
            filter
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.goal)(activeFilters) ?
          <ProgressGoalFilter {...{
            onChange: updateFilter,
            filter,
            showAllGoals: false
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.publisherGoal)(activeFilters) ?
          <ProgressGoalFilter {...{
            onChange: updateFilter,
            filter,
            showAllGoals: true
          }} /> : null
      }
      {
        fp.includes(itemFilterTypes.bookmark)(activeFilters) ?
          <ContentBookmarkFilter {...{
            onChange: updateFilter,
            filter,
          }} /> : null
      }
    </Row>
    <Row id='filter-slider-options' className='no-gutter'>
      <Button bsStyle='plain' className='full' onClick={cancelFilter}>Clear Filters</Button>
    </Row>
  </div>
})

const SelectedFilter = ({ label, unset }) => {
  return <span id='selected-filter' onClick={unset}>{label}</span>
}

const SelectedCategoryFilters = compose(
  connect(
    (state) => ({
      cpdCategorys: state.cpdCategorys.cpdCategorys
    })
  ),
  withProps(
    ({ cpdCategorys, cpdCategoryIds }) => ({
      cpdCategorys: fp.filter(
        ({ id }) => fp.includes(id)(cpdCategoryIds)
      )(cpdCategorys)
    })
  )
)(
  ({ cpdCategorys, updateFilter, cpdCategoryIds }) => {
    const unsetCpdCategory = (id) => () => {
      updateFilter({
        cpdCategoryIds: fp.filter(
          (cpdCategoryId) => cpdCategoryId !== id
        )(cpdCategoryIds)
      })
    }
    return fp.map(
      ({ id, name }) => <SelectedFilter key={`selected-category-filter-${id}`} unset={unsetCpdCategory(id)} label={name} />
    )(cpdCategorys)
  }
)

const SelectedAssociationFilters = ({ associations, updateFilter }) => {
  const unsetAssociation = (associationToCancel) => () => {
    updateFilter({
      associations: fp.filter(
        (association) => association !== associationToCancel
      )(associations)
    })
  }
  return fp.map(
    (association) => <SelectedFilter key={`selected-association-filter-${association}`} unset={unsetAssociation(association)} label={association} />
  )(associations)
}

const SelectedContentTypeFilters = ({ contentType, updateFilter }) => {
  const unsetContentType = () => {
    updateFilter({
      contentType: null
    })
  }
  return contentType ? <SelectedFilter key={`selected-content-type-filter`} unset={unsetContentType} label={contentType} /> : <div></div>
}

const SelectedBookmarkFilters = ({ isBookmarkedOnly, updateFilter }) => {
  const unsetContentType = () => {
    updateFilter({
      isBookmarkedOnly: false
    })
  }
  return isBookmarkedOnly ? <SelectedFilter key={`selected-content-type-filter`} unset={unsetContentType} label="Bookmark" /> : <div></div>
}

export const mapTimeToLable = (time) => {
  const minutes = time % 60
  const minuteLabel = minutes ? `${minutes}m` : ''
  const hours = ((time % (8 * 60)) - minutes) / 60
  const hourLabel = hours ? `${hours}h` : ''
  const days = (time - hours * 60 - minutes) / (8 * 60)
  const dayLabel = days ? `${days}d` : ''
  return `${dayLabel} ${hourLabel} ${minuteLabel}`.trim()
}

const SelectedContentLengthFilters = ({ min, max, updateFilter }) => {
  const unsetContentLength = () => {
    updateFilter({
      min: null,
      max: null
    })
  }
  const contentLength = `${mapTimeToLable(min * 60)} ~ ${mapTimeToLable(max * 60)}`
  return (min || max) ? <SelectedFilter key={`selected-content-length-filter`} unset={unsetContentLength} label={contentLength} /> : <div></div>
}

const SelectedPublisherFilters = compose(
  connect(
    (state) => ({
      publishers: getPublishers(state),
      currentBusiness: businessSelector(state)
    })
  )
)(
  ({ publisher, business, updateFilter, publishers, currentBusiness }) => {
    const unsetPublisher = () => {
      updateFilter({
        publisher: null,
        business: null
      })
    }
    const selectedPublisher = fp.flow(
      fp.filter(
        ({ id }) => id === publisher
      ),
      fp.map('name')
    )(publishers)
    const selectedBusiness = fp.flow(
      fp.filter(
        ({ id }) => id === business
      ),
      fp.map('name')
    )(currentBusiness ? [currentBusiness] : [])

    const publisherLabel = [...selectedPublisher, ...selectedBusiness].join(',')
    return (publisher || business) ? <SelectedFilter key={`selected-publisher-filter`} unset={unsetPublisher} label={publisherLabel} /> : <div></div>
  }
)

const SelectedStatusFilters = ({ status, updateFilter }) => {
  return fp.map(
    (statusSelected) => {
      const unsetStatus = () => {
        updateFilter({
          status: fp.filter(
            (s) => statusSelected !== s
          )(status),
        })
      }
      const label = `${fp.get(`${statusSelected}.value`)(contentStatus)}`
      return <SelectedFilter key={`selected-status-filter-${statusSelected}`} unset={unsetStatus} label={label} />
    }
  )(status)
}

const SelectedGoalFilters = compose(
  connect(
    (state) => ({
      goals: fp.flow(
        fp.flatMap('goals'),
        fp.uniqBy('id')
      )(state.industry.industriesWithCpdCategorys)
    })
  ),
  withProps(
    ({ goals, goalIds }) => ({
      goals: fp.filter(
        ({ id }) => fp.includes(id)(goalIds)
      )(goals)
    })
  )
)(
  ({ goals, updateFilter, goalIds }) => {
    const unsetGoal = (id) => () => {
      updateFilter({
        goalIds: fp.filter(
          (goalId) => goalId !== id
        )(goalIds)
      })
    }
    return fp.map(
      ({ id, name }) => <SelectedFilter key={`selected-goal-filter-${id}`} unset={unsetGoal(id)} label={name} />
    )(goals)
  }
)

const SelectedFilters = ({ filter, updateFilter }) => {
  return [
    <SelectedCategoryFilters key='selected-category-filters' updateFilter={updateFilter} {...filter} />,
    <SelectedAssociationFilters key='selected-assocation-filters' updateFilter={updateFilter} {...filter} />,
    <SelectedContentTypeFilters key='selected-content-type-filters' updateFilter={updateFilter} {...filter} />,
    <SelectedContentLengthFilters key='selected-content-length-filters' updateFilter={updateFilter} {...filter} />,
    <SelectedPublisherFilters key='selected-publisher-filters' updateFilter={updateFilter} {...filter} />,
    <SelectedStatusFilters key='selected-status-filters' updateFilter={updateFilter} {...filter} />,
    <SelectedGoalFilters key='selected-goal-filters' updateFilter={updateFilter} {...filter} />,
    <SelectedBookmarkFilters key='selected-bookmark-filters' updateFilter={updateFilter} {...filter} />
  ]
}

const FilterToggler = ({ toggle, active, filter, cancelFilter, updateFilter }) => {
  const {
    cpdCategoryIds,
    associations,
    contentType,
    min,
    max,
    status,
    isBookmarkedOnly,
  } = filter
  const cpdCategoryIdsApplied = cpdCategoryIds && cpdCategoryIds.length > 0
  const associationsApplied = associations && associations.length > 0
  const contentTypeApplied = contentType
  const contentLengthApplied = min || max
  const statusApplied = status && status.length > 0
  const filtersApplied = cpdCategoryIdsApplied || associationsApplied || contentTypeApplied || contentLengthApplied || statusApplied || isBookmarkedOnly
  return <span className='flexbox'>
    <div key='filter-toggler' id='filter-toggler' className={classNames('flexbox nowrap', { active, filtersApplied })} onClick={toggle}>
      <div id='filter-icon'><FiFilter /></div>
      <div id='filter-label'>Filter</div>
    </div>
    <SelectedFilters filter={filter} updateFilter={updateFilter} />
    {
      filtersApplied ?
        <span id='clear-filters-button'>
          <Button className='full' bsStyle='plain' onClick={cancelFilter}>Clear Filters</Button>
        </span> :
        null
    }
  </span>
}

const FilterGroup = ({
  toggleSlider,
  showSlider,
  updateFilter,
  cancelFilter,
  applyFilter,
  clearCounter,
  activeFilters,
  filter,
  publisherOnly
}) => {
  return <div id='filter-group'>
    <FilterToggler
      toggle={toggleSlider}
      active={showSlider}
      filter={filter}
      cancelFilter={cancelFilter}
      updateFilter={updateFilter}
    />
    <FilterSlider {...{
      show: showSlider,
      updateFilter,
      applyFilter,
      cancelFilter,
      clearCounter,
      activeFilters,
      toggleSlider,
      publisherOnly,
      filter
    }} />
  </div>
}

export default compose(
  withStateHandlers(
    {
      showSlider: false,
      filter: {},
      clearCounter: 0
    },
    {
      toggleSlider: ({ showSlider }) => () => ({
        showSlider: !showSlider
      }),
      updateFilter: ({ filter }) => (newFilter) => ({
        filter: {
          ...filter,
          ...newFilter
        }
      }),
      setFilter: () => (filter) => ({ filter }),
      clearFilter: ({ clearCounter }) => () => ({
        filter: {},
        clearCounter: clearCounter + 1
      })
    }
  ),
  withHandlers({
    applyFilter: ({ filter, onApply }) => () => {
      onApply(filter)
    },
    cancelFilter: ({ clearFilter, onApply }) => () => {
      clearFilter()
      onApply({})
    }
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        filter,
        applyFilter
      } = this.props

      if (!fp.isEqual(filter)(prevProps.filler)) {
        applyFilter()
      }
    }
  })
)(FilterGroup);


// WEBPACK FOOTER //
// ./src/containers/components/filters/filter-group.js